import { useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { JOURNAL_CATEGORIES, FEATURED_ARTICLE, ARTICLES } from "../data/content";

const Journal = () => {
  const [activeCat, setActiveCat] = useState("All Articles");

  return (
    <div>
      <PageHero
        overline="Journal"
        title="The NARA Journal."
        subtitle="Evidence-led insights on gut health, longevity, and daily wellness."
      />

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid lg:grid-cols-12 gap-10">
        {/* SIDEBAR */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-28 space-y-8">
            <div>
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400 mb-4">Categories</p>
              <ul className="space-y-1">
                {JOURNAL_CATEGORIES.map((c) => (
                  <li key={c.label}>
                    <button
                      onClick={() => setActiveCat(c.label)}
                      data-testid={`journal-cat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeCat === c.label ? "bg-royal text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                      <span>{c.label}</span>
                      <span className={activeCat === c.label ? "text-sky-200" : "text-slate-300"}>{c.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-[#FAFBFD] border border-slate-100 p-7 text-center">
              <img src="/images/brand/logo_stacked.png" alt="NARA Nutrition" className="h-24 w-auto mx-auto" />
              <p className="font-heading font-semibold text-navy-900 mt-4">Science. Purity. Daily Wellness.</p>
              <Link to="/about" className="mt-2 inline-flex items-center gap-1 text-royal text-sm font-semibold hover:gap-2 transition-all">
                Our Philosophy <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </aside>

        {/* CONTENT */}
        <div className="lg:col-span-9 space-y-12">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.18em] uppercase text-royal mb-4">Featured Article</p>
            <article className="group grid md:grid-cols-2 rounded-3xl overflow-hidden border border-slate-100 bg-white hover:shadow-xl transition-all duration-300">
              <div className="relative min-h-[280px] overflow-hidden">
                <img src={FEATURED_ARTICLE.image} alt={FEATURED_ARTICLE.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-bold tracking-widest uppercase text-royal">{FEATURED_ARTICLE.category}</span>
                  <span className="flex items-center gap-1 text-slate-400"><Clock className="w-3.5 h-3.5" /> {FEATURED_ARTICLE.read}</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy-900 mt-4 leading-tight">{FEATURED_ARTICLE.title}</h2>
                <p className="text-slate-500 mt-3 leading-relaxed">{FEATURED_ARTICLE.subtitle}</p>
                <button data-testid="featured-read-article" className="mt-6 self-start inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-navy-800 transition-colors">
                  Read Article <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <article className="group rounded-3xl overflow-hidden border border-slate-100 bg-white hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="font-bold tracking-widest uppercase text-royal">{a.category}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-400">{a.read}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-navy-900 mt-3 leading-snug flex-1">{a.title}</h3>
                    <button className="mt-4 self-start inline-flex items-center gap-1 text-royal text-sm font-semibold hover:gap-2 transition-all">
                      Read Article <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Journal;
